import * as yup from 'yup'

import { EActionsKey, THeadCellWithActions } from '~/components/table/models'
import {
  EAgentKeys,
  TAgent,
  TAssistantTableData,
  TCreateAssistant,
} from '~/features/agents/models'
import { voiceOptions } from '~/features/settings/components/phoneText/phoneTextForm/utils'
import {
  EAgentLanguage,
  EEmailStatus,
  ELoopingAudio,
  EPhoneStatus,
  ETextStatus,
} from '~/features/settings/models'
import { EServerPath } from '~/models/api'
import {
  EAgentNotifyWhen,
  ELocationTypeValues,
  ENotificationType,
  EPersonalityValues,
  EScheduleValues,
  ETextMethodValues,
} from '~/models/features'

export const queryKeys = {
  adminAgents: (filters: object = {}) => [EServerPath.adminAgents, filters],
  agents: (filters: object = {}) => [EServerPath.agents, filters],
  agent: (agentId: string) => [...queryKeys.agents(), agentId],
  agentsByOrganization: (organizationId: string) => [
    queryKeys.agents({ organizationId }),
  ],
  agentPhoneNumber: (agentId: string) => [EServerPath.phoneNumber, { agentId }],
  chatbotSettings: (agentId: string) => [
    EServerPath.chatbotSettings,
    { agentId },
  ],
  agentSignature: (agentId: string) => [
    EServerPath.agentEmailSignature,
    { agentId },
  ],
  agentPhoneSettings: (agentId: string) => [
    EServerPath.agentPhoneSettings,
    { agentId },
  ],
}

export const getDefaultAgentData = (existingAgentData?: TAgent): TAgent => {
  const agent = existingAgentData || ({} as TAgent)

  return {
    ...agent,
    [EAgentKeys.id]: agent[EAgentKeys.id],
    [EAgentKeys.agentEmail]: agent[EAgentKeys.agentEmail] || null,
    [EAgentKeys.emailStatus]:
      agent[EAgentKeys.emailStatus] || EEmailStatus.active,
    [EAgentKeys.agentName]: agent[EAgentKeys.agentName] || '',
    [EAgentKeys.orgId]: agent[EAgentKeys.orgId] || '',
    [EAgentKeys.Org]: agent[EAgentKeys.Org],
    [EAgentKeys.businessName]: agent[EAgentKeys.businessName] || '',
    [EAgentKeys.service]: agent[EAgentKeys.service] || '',
    [EAgentKeys.siteLink]: agent[EAgentKeys.siteLink] || '',
    [EAgentKeys.locationType]:
      agent[EAgentKeys.locationType] || ELocationTypeValues.anywhere,
    [EAgentKeys.location]: agent[EAgentKeys.location] || '',
    [EAgentKeys.pricing]: agent[EAgentKeys.pricing] || '',
    [EAgentKeys.questions]: agent[EAgentKeys.questions] || [],
    [EAgentKeys.facts]: agent[EAgentKeys.facts] || [],
    [EAgentKeys.calendlyLink]: agent[EAgentKeys.calendlyLink] || '',
    [EAgentKeys.cta]: agent[EAgentKeys.cta],
    [EAgentKeys.ctaText]: agent[EAgentKeys.ctaText] || null,
    [EAgentKeys.customURL]: agent[EAgentKeys.customURL] || [],
    [EAgentKeys.emailConsent]: agent[EAgentKeys.emailConsent] || '',
    [EAgentKeys.mobileConsent]: agent[EAgentKeys.mobileConsent] || false,
    [EAgentKeys.notifyEmail]: agent[EAgentKeys.notifyEmail] || '',
    [EAgentKeys.notifyMobile]: agent[EAgentKeys.notifyMobile] || '',
    [EAgentKeys.notifyType]:
      agent[EAgentKeys.notifyType] || ENotificationType.email,
    [EAgentKeys.notifyWhen]:
      agent[EAgentKeys.notifyWhen] || EAgentNotifyWhen.afterAllChats,
    [EAgentKeys.personality]:
      agent[EAgentKeys.personality] || EPersonalityValues.professional,
    [EAgentKeys.phoneIntro]: agent[EAgentKeys.phoneIntro] || '',
    [EAgentKeys.phoneNumber]: agent[EAgentKeys.phoneNumber] || '',
    [EAgentKeys.agentLanguage]:
      agent[EAgentKeys.agentLanguage] || EAgentLanguage.English,
    [EAgentKeys.logoImgUrl]: agent[EAgentKeys.logoImgUrl] || null,
    [EAgentKeys.scheduleType]:
      agent[EAgentKeys.scheduleType] || EScheduleValues.askForAvailability,
    [EAgentKeys.zapierId]: agent[EAgentKeys.zapierId] || '',
    [EAgentKeys.customLinks]: agent[EAgentKeys.customLinks] || [],
    [EAgentKeys.chatIntro]: agent[EAgentKeys.chatIntro] || '',
    [EAgentKeys.chatbotImgURL]: agent[EAgentKeys.chatbotImgURL] || undefined,
    [EAgentKeys.showLibby]: agent[EAgentKeys.showLibby] || false,
    [EAgentKeys.showBranding]: agent[EAgentKeys.showBranding] || false,
    [EAgentKeys.chatFriendlyUrl]: agent[EAgentKeys.chatFriendlyUrl] || '',
    [EAgentKeys.phoneStatus]:
      agent[EAgentKeys.phoneStatus] || EPhoneStatus.active,
    [EAgentKeys.phoneVoice]: agent[EAgentKeys.phoneVoice] || voiceOptions[0].id,
    [EAgentKeys.phoneIntroRecording]:
      agent[EAgentKeys.phoneIntroRecording] || '',
    [EAgentKeys.textMethod]:
      agent[EAgentKeys.textMethod] || ETextMethodValues.SMS,
    [EAgentKeys.scrapeStatus]: agent[EAgentKeys.scrapeStatus] || undefined,
    [EAgentKeys.customInstructions]:
      agent[EAgentKeys.customInstructions] || null,
    [EAgentKeys.escalations]: agent[EAgentKeys.escalations] || [],
    [EAgentKeys.chatStatus]: agent[EAgentKeys.chatStatus] || undefined,
    [EAgentKeys.textStatus]:
      agent[EAgentKeys.textStatus] || ETextStatus.disabled,
    [EAgentKeys.loopingAudio]:
      agent[EAgentKeys.loopingAudio] || ELoopingAudio.off,
    [EAgentKeys.createdDate]: agent.createdDate || undefined,
    [EAgentKeys.updatedDate]: agent.updatedDate || undefined,
  }
}

export const getStringFromAgentFacts = (facts: TAgent['facts']): string[] => {
  return (facts || []).map((fact) => {
    if (typeof fact === 'string') {
      return fact
    }

    return fact.text
  })
}

export function getFormattedPhoneNumber(phoneNumberString: string) {
  const cleaned = `${phoneNumberString}`.replace(/\D/g, '')
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)

  if (match) {
    return `(${match[2]}) ${match[3]}-${match[4]}`
  }

  return ''
}

export const getAssistantListFilteredData = (
  agents: TAgent[],
  searchValue: string,
) =>
  agents.filter(
    (agent) =>
      !searchValue ||
      (agent[EAgentKeys.agentReferenceName] &&
        agent[EAgentKeys.agentReferenceName]
          .toLowerCase()
          .includes(searchValue.toLowerCase())) ||
      (agent[EAgentKeys.agentEmail] &&
        agent[EAgentKeys.agentEmail]
          .toLowerCase()
          .includes(searchValue.toLowerCase())) ||
      (agent[EAgentKeys.agentEmailCustom] &&
        agent[EAgentKeys.agentEmailCustom]
          .toLowerCase()
          .includes(searchValue.toLowerCase())) ||
      (agent[EAgentKeys.phoneNumber] &&
        agent[EAgentKeys.phoneNumber]
          .toLowerCase()
          .includes(searchValue.toLowerCase())) ||
      (agent[EAgentKeys.chatFriendlyUrl] &&
        agent[EAgentKeys.chatFriendlyUrl]
          .toLowerCase()
          .includes(searchValue.toLowerCase())),
  )

export const assistantListHeadCells: THeadCellWithActions<TAssistantTableData> =
  [
    {
      id: EAgentKeys.agentReferenceName,
      label: 'Assistant Name',
      isSortable: true,
    },
    { id: EAgentKeys.id, label: 'Chat', isSortable: false },
    { id: EAgentKeys.phoneNumber, label: 'Phone', isSortable: true },
    { id: EAgentKeys.agentEmail, label: 'Email', isSortable: true },
    { id: EActionsKey.actions, label: '', isSortable: false },
  ]

export const createAssistantValidationSchema = yup.object<TCreateAssistant>({
  [EAgentKeys.agentReferenceName]: yup
    .string()
    .required('Assistant name is required'),
  [EAgentKeys.businessName]: yup.string().required('Business name is required'),
  [EAgentKeys.service]: yup.string().required('Service is required'),
})
